import classNames from 'classnames';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField, Input, toNumberString } from '@client/toolkit';

import { EconomyEditableCard } from './economy-editable-card.component';
import { SettingsRow } from './settings-row.component';
import {
  ECONOMY_FIELD_SIZE,
  ECONOMY_HINT_CLASS,
  ECONOMY_ROW_LABEL_CLASS,
  ECONOMY_STACK_CLASS,
  ECONOMY_STACK_ROW_CLASS,
  ECONOMY_TICK_CLASS,
  ECONOMY_VALUE_CLASS,
} from '../economy-theme';
import { MANAGEMENT_MODELS, MockPortfolioEconomySettings } from '../mock/portfolio-economy.mock';
import {
  globalStandardsSchema,
  GlobalStandardsFormPath,
  GlobalStandardsFormValues as FormValues,
  toGlobalStandardsFormValues,
} from '../portfolio-economy-settings.form';

/** Factors read back as `1,0` rather than `1`, matching how they are entered. */
const toFactorText = (value: unknown) =>
  toNumberString(Number(value), '—', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Reads the form the card owns, so it has to live inside its `FormProvider`. */
function GlobalStandardsFields({ editing }: { editing: boolean }) {
  const { t } = useTranslation('economy');
  const { control } = useFormContext<FormValues>();

  const managementModel = useWatch({ control, name: 'managementModel' });
  const marginPercent = useWatch({ control, name: 'marginPercent' });
  const markupRate = useWatch({ control, name: 'markupRate' });
  const hasMargin = managementModel === 'ownerMargin';

  // Driven by the form array rather than ALLOCATION_KEY_IDS: binding by the constant's
  // index would silently mislabel every row if an API ever returned another order.
  const allocationKeys = useWatch({ control, name: 'allocationKeys' }) ?? [];

  return (
    <>
      <SettingsRow label={t('settings.globalStandards.managementModel')} align="top">
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>{t(`managementModel.${managementModel}`)}</span>
        ) : (
          <FormField<FormValues, 'managementModel'>
            name="managementModel"
            render={({ field }) => (
              <div className={ECONOMY_STACK_CLASS}>
                {MANAGEMENT_MODELS.map((model) => (
                  <label key={model} className={classNames(ECONOMY_STACK_ROW_CLASS, 'cursor-pointer gap-2')}>
                    <input
                      type="radio"
                      name={field.name}
                      value={model}
                      checked={field.value === model}
                      onBlur={field.onBlur}
                      onChange={() => field.onChange(model)}
                      className={ECONOMY_TICK_CLASS}
                    />
                    <span className={ECONOMY_VALUE_CLASS}>{t(`managementModel.${model}`)}</span>
                  </label>
                ))}
              </div>
            )}
          />
        )}

        <div className={classNames(ECONOMY_STACK_ROW_CLASS, 'mt-3 gap-2', editing && 'pl-5')}>
          <span className={ECONOMY_ROW_LABEL_CLASS}>{t('settings.globalStandards.marginPercent')}:</span>
          {hasMargin && editing ? (
            <div className="w-[96px]">
              <FormField<FormValues, 'marginPercent'>
                name="marginPercent"
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    type="number"
                    step="0.1"
                    size={ECONOMY_FIELD_SIZE}
                    aria-label={t('settings.globalStandards.marginPercent')}
                    className="text-right"
                  />
                )}
              />
            </div>
          ) : (
            <span className={ECONOMY_VALUE_CLASS}>{hasMargin ? `${marginPercent} %` : '—'}</span>
          )}
        </div>
      </SettingsRow>

      <SettingsRow label={t('settings.globalStandards.markupRate')}>
        <div className="flex flex-wrap items-center gap-3">
          {!editing ? (
            <span className={ECONOMY_VALUE_CLASS}>{markupRate} %</span>
          ) : (
            <>
              <div className="w-[96px]">
                <FormField<FormValues, 'markupRate'>
                  name="markupRate"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      step="0.1"
                      size={ECONOMY_FIELD_SIZE}
                      aria-label={t('settings.globalStandards.markupRate')}
                      className="text-right"
                    />
                  )}
                />
              </div>
              <span className={ECONOMY_VALUE_CLASS}>%</span>
            </>
          )}
          <span className={ECONOMY_HINT_CLASS}>{t('settings.globalStandards.markupHint')}</span>
        </div>
      </SettingsRow>

      <SettingsRow label={t('settings.globalStandards.allocationKey')} align="top">
        <div className={ECONOMY_STACK_CLASS}>
          {allocationKeys.map(({ id: keyId, factor }, index) => (
            <div key={keyId} className={classNames(ECONOMY_STACK_ROW_CLASS, 'gap-4')}>
              <span className={classNames('flex-1', ECONOMY_VALUE_CLASS)}>{t(`allocationKey.${keyId}`)}</span>
              {!editing ? (
                <span className={classNames('w-[96px] text-right', ECONOMY_VALUE_CLASS)}>{toFactorText(factor)}</span>
              ) : (
                <div className="w-[96px]">
                  <FormField<FormValues, GlobalStandardsFormPath>
                    name={`allocationKeys.${index}.factor`}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        step="0.1"
                        size={ECONOMY_FIELD_SIZE}
                        aria-label={t(`allocationKey.${keyId}`)}
                        className="text-right"
                      />
                    )}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </SettingsRow>
    </>
  );
}

export interface GlobalStandardsCardProps {
  settings: MockPortfolioEconomySettings;
  onSave: (values: FormValues) => void;
}

/** Defaults new buildings inherit: management model, markup rate and distribution factors. */
export function GlobalStandardsCard({ settings, onSave }: GlobalStandardsCardProps) {
  const { t } = useTranslation('economy');
  const defaultValues = useMemo(() => toGlobalStandardsFormValues(settings), [settings]);

  return (
    <EconomyEditableCard<FormValues>
      rowStack
      title={t('settings.globalStandards.title')}
      defaultValues={defaultValues}
      schema={globalStandardsSchema}
      onSave={onSave}
    >
      {(editing) => <GlobalStandardsFields editing={editing} />}
    </EconomyEditableCard>
  );
}

export default GlobalStandardsCard;
